import { Button } from '@/components/ui/button';
import {
  ChartLineIcon,
  DatabaseIcon,
  FlowArrowIcon,
  KeyboardIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { GlobalSearch } from './GlobalSearch';
import { MobileNavigation } from './MobileNavigation';
import { TutorialMenu } from './TutorialMenu';
import { APISource, Dataset, ResearchDomainTemplate } from '@/lib/types';

interface AppHeaderProps {
  mockAPISources: APISource[];
  allDatasets: Dataset[];
  selectedDatasets: Dataset[];
  activeTab: string;
  qualityReports: Record<string, any>;
  correlations: any[];
  appliedTemplate: ResearchDomainTemplate | null;
  tutorialsWithCallbacks: any[];
  onGlobalSearch: (query: string) => void;
  onTabChange: (tab: string) => void;
  onShowKeyboardHelp: () => void;
  onTutorialStart: (tutorial: any) => void;
  onAnalyzeCorrelations: () => void;
}

export function AppHeader({
  mockAPISources,
  allDatasets,
  selectedDatasets,
  activeTab,
  qualityReports,
  correlations,
  appliedTemplate,
  tutorialsWithCallbacks,
  onGlobalSearch,
  onTabChange,
  onShowKeyboardHelp,
  onTutorialStart,
  onAnalyzeCorrelations,
}: AppHeaderProps) {
  const getNextStepButton = () => {
    if (selectedDatasets.length === 0) {
      return (
        <Button
          onClick={() => onTabChange('templates')}
          className='gap-2'
          variant='default'
        >
          <SparkleIcon className='w-4 h-4' />
          Get Started
        </Button>
      );
    } else if (Object.keys(qualityReports).length < selectedDatasets.length) {
      return (
        <Button
          onClick={() => onTabChange('quality')}
          className='gap-2'
          variant='default'
        >
          <ChartLineIcon className='w-4 h-4' />
          Check Quality
        </Button>
      );
    } else if (selectedDatasets.length >= 2 && correlations.length === 0) {
      return (
        <Button
          onClick={onAnalyzeCorrelations}
          className='gap-2'
          variant='default'
        >
          <ChartLineIcon className='w-4 h-4' />
          Analyze Data
        </Button>
      );
    } else if (selectedDatasets.length > 0) {
      return (
        <Button
          onClick={() => onTabChange('transform')}
          className='gap-2'
          variant='default'
        >
          <FlowArrowIcon className='w-4 h-4' />
          Transform
        </Button>
      );
    }

    return null;
  };

  return (
    <header className='border-b bg-card'>
      <div className='container mx-auto px-4 sm:px-6 py-4 max-w-full'>
        <div className='flex items-center justify-between min-w-0 gap-2 sm:gap-4'>
          <div className='flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink'>
            <div className='p-2 rounded-lg bg-primary text-primary-foreground flex-shrink-0'>
              <DatabaseIcon className='w-5 h-5 sm:w-6 sm:h-6' />
            </div>
            <div className='min-w-0 hidden sm:block'>
              <h1 className='text-xl sm:text-2xl font-bold truncate'>
                DataLab Explorer
              </h1>
              <p className='text-muted-foreground text-sm truncate'>
                Learn data analysis through hands-on exploration
              </p>
            </div>
          </div>

          <div className='flex items-center gap-2 sm:gap-4 flex-shrink-0'>
            {/* Global Search */}
            <div className='hidden lg:block'>
              <GlobalSearch
                sources={mockAPISources}
                datasets={allDatasets}
                onNavigate={onGlobalSearch}
              />
            </div>

            {/* Mobile Navigation */}
            <MobileNavigation
              activeTab={activeTab}
              onTabChange={onTabChange}
              selectedDatasetCount={selectedDatasets.length}
              hasQualityReports={Object.keys(qualityReports).length > 0}
              hasCorrelations={correlations.length > 0}
            />

            <Button
              variant='outline'
              size='sm'
              onClick={onShowKeyboardHelp}
              className='gap-2 hidden sm:flex'
            >
              <KeyboardIcon className='w-4 h-4' />
              Shortcuts
            </Button>

            <TutorialMenu
              tutorials={tutorialsWithCallbacks}
              onTutorialStart={onTutorialStart}
            />

            {appliedTemplate && (
              <div className='text-right min-w-0 hidden md:block'>
                <div className='text-xs text-muted-foreground'>
                  Active Template
                </div>
                <div className='text-sm font-medium text-accent truncate'>
                  {appliedTemplate.name}
                </div>
              </div>
            )}

            {/* Next Step Helper */}
            {getNextStepButton()}
          </div>
        </div>
      </div>
    </header>
  );
}
